import React from 'react';
import '../../styles/components/empty-states.css';

interface EmptyStateProps {
  icon?: string;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

interface EmptySearchProps {
  searchTerm?: string;
  onClearSearch?: () => void;
  className?: string;
}

interface EmptyStateIllustrationProps {
  illustration?: React.ReactNode;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

interface EmptyStateButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
}

interface EmptyStateLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode;
  href: string;
  className?: string;
}

// Empty State Component
export function EmptyState({ 
  icon = '📭',
  title = 'Veri Bulunamadı',
  description = 'Bu bölümde henüz veri bulunmuyor.',
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <div className={`empty-state ${className}`}>
      <div className="empty-state-content">
        <div className="empty-state-icon">{icon}</div>
        <h3 className="empty-state-title">{title}</h3>
        <p className="empty-state-description">{description}</p>
        {action && (
          <div className="empty-state-action">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

// Empty List Component
export function EmptyList({ 
  icon = '📋',
  title = 'Liste Boş',
  description = 'Bu listede henüz öğe bulunmuyor.',
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <EmptyState
      icon={icon}
      title={title}
      description={description}
      action={action}
      className={`empty-list ${className}`}
    />
  );
}

// Empty Search Component
export function EmptySearch({ 
  searchTerm,
  onClearSearch,
  className = ''
}: EmptySearchProps) {
  return (
    <EmptyState
      icon="🔍"
      title="Arama Sonucu Bulunamadı"
      description={searchTerm ? `"${searchTerm}" için sonuç bulunamadı.` : 'Arama kriterlerinize uygun sonuç bulunamadı.'}
      action={
        onClearSearch ? (
          <button className="empty-state-btn" onClick={onClearSearch}>
            🔄 Aramayı Temizle
          </button>
        ) : undefined
      }
      className={`empty-search ${className}`}
    />
  );
}

// Empty Table Component
export function EmptyTable({ 
  title = 'Tablo Boş',
  description = 'Bu tabloda henüz veri bulunmuyor.',
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <div className={`empty-table ${className}`}>
      <div className="empty-table-content">
        <div className="empty-table-icon">📊</div>
        <h3 className="empty-table-title">{title}</h3>
        <p className="empty-table-description">{description}</p>
        {action && (
          <div className="empty-table-action">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

// Empty Card Component
export function EmptyCard({ 
  icon = '💳',
  title = 'Kart Boş',
  description = 'Bu kartta henüz içerik bulunmuyor.',
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <div className={`empty-card ${className}`}>
      <div className="empty-card-content">
        <div className="empty-card-icon">{icon}</div>
        <h3 className="empty-card-title">{title}</h3>
        <p className="empty-card-description">{description}</p>
        {action && (
          <div className="empty-card-action">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

// Empty Dashboard Component
export function EmptyDashboard({ 
  title = 'Dashboard Boş',
  description = 'Dashboard\'da henüz veri bulunmuyor.',
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <EmptyState
      icon="📈"
      title={title}
      description={description}
      action={action}
      className={`empty-dashboard ${className}`}
    />
  );
}

// Empty Notifications Component
export function EmptyNotifications({ 
  className = ''
}: { className?: string }) {
  return (
    <EmptyState
      icon="🔔"
      title="Bildirim Yok"
      description="Henüz bildiriminiz bulunmuyor."
      className={`empty-notifications ${className}`}
    />
  );
}

// Empty Messages Component
export function EmptyMessages({ 
  className = ''
}: { className?: string }) {
  return (
    <EmptyState
      icon="💬"
      title="Mesaj Yok"
      description="Henüz mesajınız bulunmuyor."
      className={`empty-messages ${className}`}
    />
  );
}

// Empty Files Component
export function EmptyFiles({ 
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <EmptyState
      icon="📁"
      title="Dosya Yok"
      description="Henüz dosya yüklenmemiş."
      action={action}
      className={`empty-files ${className}`}
    />
  );
}

// Empty Calendar Component
export function EmptyCalendar({ 
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <EmptyState
      icon="📅"
      title="Etkinlik Yok"
      description="Bu tarihte henüz etkinlik bulunmuyor."
      action={action}
      className={`empty-calendar ${className}`}
    />
  );
}

// Empty Profile Component
export function EmptyProfile({ 
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <EmptyState
      icon="👤"
      title="Profil Bilgisi Yok"
      description="Profil bilgilerinizi tamamlayın."
      action={action}
      className={`empty-profile ${className}`}
    />
  );
}

// Empty Settings Component
export function EmptySettings({ 
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <EmptyState
      icon="⚙️"
      title="Ayar Yok"
      description="Bu bölümde henüz ayar bulunmuyor."
      action={action}
      className={`empty-settings ${className}`}
    />
  );
}

// Empty State with Illustration
export function EmptyStateIllustration({ 
  illustration,
  title,
  description,
  action,
  className = ''
}: EmptyStateIllustrationProps) {
  return (
    <div className={`empty-state-illustration ${className}`}>
      <div className="empty-state-illustration-content">
        {illustration && (
          <div className="empty-state-illustration-image">
            {illustration}
          </div>
        )}
        <h3 className="empty-state-illustration-title">{title}</h3>
        <p className="empty-state-illustration-description">{description}</p>
        {action && (
          <div className="empty-state-illustration-action">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

// Empty State Button
export function EmptyStateButton({ 
  children, 
  onClick, 
  variant = 'primary',
  className = ''
}: EmptyStateButtonProps) {
  return (
    <button 
      className={`empty-state-btn empty-state-btn-${variant} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// Empty State Link
export function EmptyStateLink({ 
  children, 
  href, 
  className = ''
}: EmptyStateLinkProps) {
  return (
    <a 
      className={`empty-state-link ${className}`}
      href={href}
    >
      {children}
    </a>
  );
}

// Empty State Types
export const EMPTY_STATE_TYPES = {
  LIST: 'list',
  TABLE: 'table',
  CARD: 'card',
  DASHBOARD: 'dashboard',
  SEARCH: 'search',
  NOTIFICATIONS: 'notifications',
  MESSAGES: 'messages',
  FILES: 'files',
  CALENDAR: 'calendar',
  PROFILE: 'profile',
  SETTINGS: 'settings'
} as const;

// Empty State Icons
export const EMPTY_STATE_ICONS = {
  LIST: '📋',
  TABLE: '📊',
  CARD: '💳',
  DASHBOARD: '📈',
  SEARCH: '🔍',
  NOTIFICATIONS: '🔔',
  MESSAGES: '💬',
  FILES: '📁',
  CALENDAR: '📅',
  PROFILE: '👤',
  SETTINGS: '⚙️',
  DEFAULT: '📭'
} as const;

export default {
  EmptyState,
  EmptyList,
  EmptySearch,
  EmptyTable,
  EmptyCard,
  EmptyDashboard,
  EmptyNotifications,
  EmptyMessages,
  EmptyFiles,
  EmptyCalendar,
  EmptyProfile,
  EmptySettings,
  EmptyStateIllustration,
  EmptyStateButton,
  EmptyStateLink,
  EMPTY_STATE_TYPES,
  EMPTY_STATE_ICONS
};

